import React from 'react';
import { toSafeAlertString } from '../lib/safeAlertString';

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

function normalizeBoundaryError(error: unknown): Error {
  if (error instanceof Error) {
    // `new Error(someObject)` yields this useless message; recover when possible.
    if (error.message === '[object Object]') {
      const c = (error as { cause?: unknown }).cause;
      return new Error(
        c != null ? toSafeAlertString(c, 'Something went wrong') : 'Something went wrong'
      );
    }
    return error;
  }
  if (typeof error === 'string' && error.trim()) return new Error(error.trim());
  return new Error(toSafeAlertString(error, 'Something went wrong'));
}

export class ErrorBoundary extends React.Component<
  { children: React.ReactNode },
  ErrorBoundaryState
> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: unknown): ErrorBoundaryState {
    return { hasError: true, error: normalizeBoundaryError(error) };
  }

  componentDidCatch(error: unknown, info: React.ErrorInfo) {
    if (process.env.NODE_ENV === 'development') {
      console.error('ErrorBoundary caught:', normalizeBoundaryError(error), info);
    }
  }

  handleReload = () => {
    this.setState({ hasError: false, error: null });
    window.location.href = '/district';
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="mobile-page bg-black text-white flex items-center justify-center px-4">
          <div className="max-w-md w-full text-center">
            <h1 className="text-5xl font-bold tracking-tighter mb-2">
              DISTRICT<span className="text-red-500">88</span>
            </h1>
            <div className="mt-8 bg-gray-900/80 border border-gray-800 rounded-2xl p-8">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-red-500/10 flex items-center justify-center">
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-red-400">
                  <circle cx="12" cy="12" r="10" />
                  <line x1="12" y1="8" x2="12" y2="12" />
                  <line x1="12" y1="16" x2="12.01" y2="16" />
                </svg>
              </div>
              <h2 className="text-xl font-bold mb-2">Something went wrong</h2>
              <p className="text-gray-400 text-sm mb-6">
                The District hit a snag. This has been noted and we're on it.
              </p>
              <button
                onClick={this.handleReload}
                className="px-6 py-3 bg-red-600 hover:bg-red-700 text-white font-semibold rounded-xl transition-all"
              >
                Back to District
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
